/**
 * Kiosk Portal Store Configuration
 *
 * Minimal store for kiosk portal (auth only for now)
 */

import {
  configureStore,
  combineReducers,
  createSagaMiddleware,
  all,
  fork,
  configureLocaleProvider,
  configureTenantIdProvider,
} from '@spcc/core'

// Auth domain
import { authReducer, authSaga } from '@spcc/domains/auth'

// Portal init (calls /api/users/me and distributes admin context, feature flags, etc.)
import { initReducer, initSaga } from '@spcc/domains/portal-init'

// Admin context (optional - for tenant switching if needed)
import { adminContextReducer, adminContextActions, selectTenantContext } from '@spcc/domains/admin-context'

// Shipments domain (Scan feature)
import { scanReducer, scanSaga, weighbridgeReducer, weighbridgeSaga } from '@spcc/domains/shipments'
// Support domain
import { supportReducer, supportSaga } from '@spcc/domains/support'

// Queue domain (Join Queue feature)
import { queueReducer, queueSaga } from '@spcc/domains/queue'
import { citiesReducer, citiesSaga } from '@spcc/domains/cities'
import { getStoredLanguage } from '@/lib/language-preferences'
import { kioskConfigReducer, kioskConfigSaga } from './kiosk-config.slice'
import { authMonitoringSaga } from './auth-monitoring.sagas'

// Root reducer (minimal for kiosk)
const rootReducer = combineReducers({
  auth: authReducer,
  init: initReducer,
  // Admin context for tenant switching (if needed)
  adminContext: adminContextReducer,
  // Scan feature state
  scan: scanReducer,
  // Weighbridge feature state
  weighbridge: weighbridgeReducer,
  // Queue management
  queue: queueReducer,
  // City selection for orders missing city
  cities: citiesReducer,
  // Support
  support: supportReducer,
  // Kiosk queue config
  kioskConfig: kioskConfigReducer,
})

// Root saga
function* rootSaga() {
  yield all([
    fork(authSaga),
    fork(initSaga),
    fork(scanSaga),
    fork(weighbridgeSaga),
    fork(queueSaga),
    fork(citiesSaga),
    fork(supportSaga),
    fork(kioskConfigSaga),
    fork(authMonitoringSaga),
  ])
}

// Create saga middleware
const sagaMiddleware = createSagaMiddleware()

// Create store
export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      thunk: false, // We use sagas instead
    }).concat(sagaMiddleware),
  devTools: import.meta.env.DEV,
})

// Run the root saga
sagaMiddleware.run(rootSaga)

// Restore admin context tenant selection from localStorage (if admin-context is used)
store.dispatch(adminContextActions.restoreFromStorage())

// Configure HTTP client to include X-Tenant-Id header when admin has selected a tenant
// This ensures all API calls are scoped to the selected tenant
configureTenantIdProvider({
  getTenantId: () => selectTenantContext(store.getState()),
})

configureLocaleProvider({
  getLocale: () => getStoredLanguage(),
})

// Export types
export type RootState = ReturnType<typeof rootReducer>
export type AppDispatch = typeof store.dispatch
